import graphqlify from 'graphqlify';

import template from './NavigationBarView.hbs';
import './NavigationBarView.less';
import {Alias} from '../../../js/alias.js';
import {router} from '../../../js/router.js';
import {ClickoutBehavior} from '../../behavior/ClickoutBehavior';
import {BaseView, Prototype} from '../BaseView';
import {CountryUtils} from '../../utils/CountryUtils';
import {CURRENT_FARM_FEATURES, FARMS} from '../../../js/resources/SessionObjects';
import {SessionStorageUtil} from '../../utils/SessionStorageUtil';
import {FarmCollection} from '../../collection/farms/FarmCollection';
import {FarmAddModal} from '../../../layout/FarmLayout/FarmAddModal/FarmAddModal';
import {RequestAccessModalView} from '../../../layout/InviteCollaboratorsLayout/RequestAccessModalView/RequestAccessModalView';
import {PermissionRequestsCollection} from '../../collection/users/PermissionRequestsCollection';
import {PermissionCollection} from '../../collection/users/PermissionCollection';
import {BaseFilterModel} from '../../model/filter/BaseFilterModel';
import {UserModel} from '../../model/users/UserModel';
import farmSelectItemTpl from './FarmSelectItem/FarmSelectItem.hbs';
import farmSelectValueTpl from './FarmSelectItem/FarmSelectValue.hbs';
import { CircleNumberView } from '../CircleNumberView/CircleNumberView';
import { UPDATE_PENDING_PERMISSION_REQUEST_COUNT } from '../../constants';
import {PermissionRequestModel} from '../../model/users/PermissionRequestModel';
import {FarmModel} from '../../model/farms/FarmModel';

const HIDDEN_ROUTES = [
    Alias.LOGIN,
    Alias.REGISTRATION,
    Alias.REGISTRATION_2019,
    Alias.REGISTRATION_HF_CONTRACTOR,
    Alias.REGISTRATION_HEALTHY_FIELDS,
    Alias.REGISTRATION_HEALTHY_FIELDS_CONTRACTOR,
    Alias.REGISTRATIONS_CLOSED,
    Alias.RESET_PASS,
    Alias.FORGOT_PASS,
    Alias.PAGE_NOT_FOUND,
    Alias.NOT_AUTHORIZED,
    Alias.UNDER_CONSTRUCTION,
    Alias.TASK_OBSERVATION_CREATE,
    Alias.TC_ACCEPTED,
    Alias.EMAIL_VERIFIED,
    Alias.TASK_SPRAYING_CREATE,
    Alias.TASK_SPRAYING_EDIT,
    Alias.TASK_HERBICIDE_CREATE,
    Alias.TASK_HERBICIDE_EDIT,
    Alias.TASK_DRONE_FLIGHT_CREATE,
    Alias.TASK_SCOUTING_CREATE,
    Alias.TASK_SCOUTING_EDIT,
    Alias.ASSIGN_CS,
    Alias.EDIT_CS,
    Alias.BOUNDARY_DETECTION,
    Alias.BOUNDARY_DETECTION_SUCCESS,
    Alias.DRAW,
    Alias.EDIT_FIELD,
    Alias.FIELD_UPLOAD,
    Alias.HEALTHY_FIELDS_REQUEST,
    Alias.HEALTHY_FIELDS_CONTRACTOR_REQUEST,
    Alias.TASK_NUTRITION_CREATE,
    Alias.TASK_NUTRITION_EDIT,
    Alias.SHOP_ORDER,
    Alias.SHOP_PAYMENT_SUCCESS,
    Alias.SHOP_PAYMENT_FAILED,
    Alias.FORWARD_LAYOUT,
    Alias.V2_APP,
    Alias.AB_LINES,
    Alias.EDIT_AB_LINES,
    Alias.DRAW_AB_LINES,
    Alias.HEALTHY_FIELDS_SERVICES
];


/**
 * @class NavigationBar
 * @extends BaseView
 *
 * @property {ResourceLocator} model
 */
@Prototype({
    template,
    behaviors: [{
        behaviorClass: ClickoutBehavior,
        andSelf: false
    }],
    regions: {
        tutorial: '.help-content',
        pendingPermissionRequestCount: '.pending-permission-request-count',
    },
    ui: {
        settings: '.settings-toggle',
        homeButton: '.home-button',
        navItem: '[data-nav]',
    },
    events: {
        'click @ui.settings': 'onClickSettings',
        'click @ui.navItem': 'onClickNavItem',
        'click @ui.homeButton': 'onClickNavItem',
    },
    modelEvents: {
        'change:user': 'onUserModelChange'
    },
    GIGYA_UUID: 'ee886ccde7da42dfb60de81e9f55ea32',
    GIGYA_UUID_QA: 'fabc81ff02b945a78dddc4e2b3cb05ba'
})
export class NavigationBarView extends BaseView {

    className() {
        
        return this.checkIsSettingsUrl()
            ? 'header2 header2--white'
            : 'header2';
    }

    initialize () {

        this.navConfig = {
            'nav_status-home': 'field/status',
            'nav_status': 'field/status',
            'nav_tasks': 'task',
            'nav_weather': 'field/weather',
            'nav_history': 'fields',
            'nav_notes': 'notes',
            'nav_analytics': 'fdl',
            'nav_sustainability': 'sustainability',
            'nav_healthy-fields': 'healthy-fields/overview',
            'nav_shop': 'shop/products'
        };

        super.initialize();

        this._state = new Backbone.Model({
            helpVisible: false,
            settingsVisible: false,
            selectedFarm: undefined
        });

        this.onUpdateFarms = _.debounce(_.bind(this.onUpdateFarms, this), 150);

        this._state.on('change:selectedFarm', this.onFarmChange, this);
        this._state.on('change:selectedFarm', this.clearLocalStorage, this);
        this._state.on('change:settingsVisible change:selectedFarm', this.onStateChange, this);
        this.listenTo(FARMS, 'change', this.onUpdateFarms);

        Radio.channel('ROUTER').on('ROUTE:FINISH', this.onRouteFinish, this);
        Radio.channel('NOTIFICATION').on('MODAL', this.closeAll, this);
        Radio.channel('NOTIFICATION').on('MODAL2', this.closeAll, this);
        Radio.channel('NOTIFICATION').on('NOTIFY', this.closeAll, this);
        Radio.channel('NOTIFICATION').on('NOTIFY_ACTION', this.closeAll, this);
        Radio.channel('NOTIFICATION').on(
            UPDATE_PENDING_PERMISSION_REQUEST_COUNT,
            this.updatePendingPermissionRequestCounter,
            this
        );
    }

    clearLocalStorage () {

        if (this.model && this.model.get('user') && this.model.get('user').get('settings')) {

            let lastFarmSelectedUuid = this.model.get('user').get('settings')
                .getSetting('TAMS', 'LAST_FARM_SELECTED').get('value');

            if (lastFarmSelectedUuid && this._state.get('selectedFarm') &&
                (lastFarmSelectedUuid !== this._state.get('selectedFarm'))) {

                // Clear any previous farm's cart items
                localStorage.removeItem('SHOP_PACKAGE_UUIDS');
                localStorage.removeItem('SHOP_VOUCHER');
            }
        }
    }

    getPendingPermissionRequestCount() {
        
        const num = this.permissionRequests
            && this.permissionRequests.filter({status: PermissionRequestModel.STATUS.PENDING,
                requesteeId: this._user.get('uuid')}).length
            || 0;

        return num;
    }

    updatePendingPermissionRequestCounter() {

        if(this.permissionRequests) {
            this.permissionRequests
                .update()
                .done(() => {
                    this.getRegion('pendingPermissionRequestCount').show(new CircleNumberView({
                        num: this.getPendingPermissionRequestCount(),
                    }));
                });
        }
    }

    onUpdateFarms () {

        this.safeRender();
    }

    onUserModelChange() {

        if(_.isUndefined(this._user)) {

            this._user = this.model.get('user');

            this.listenTo(this._user, 'change:firstName', this.safeRender);

            this.updateFarms();
        } else {

            this.updateFarms();
        }
    }

    updateFarms () {

        let farmDepsFetched;

        if (this._user.get('uuid')) {

            farmDepsFetched = this.fetchFarms()
                .done(() => {

                    FARMS.add({
                        name: __('NAVIGATION_BAR_VIEW.CROSSFARM_DASHBOARD'),
                        uuid: 'dashboard'
                    });
                });

            this.permissionRequests = new PermissionRequestsCollection();

            $.when(farmDepsFetched,
                this.permissionRequests.update())
                .done(() => {
                    FARMS.comparator = FarmCollection.cmpByFarmName;
                    FARMS.sort();
                    this.safeRender();
                })
        }
    }

    fetchFarms () {

        let queryObject = {};

        queryObject.farmsV2 = {
            params: {uuids: []},
            fields: {
                uuid: {},
                name: {},
                timezone: {},
                latitude: {},
                longitude: {},
                etag: {},
                creator: {
                    fields: {
                        uuid: {},
                        firstName: {},
                        lastName: {},
                        email: {}
                    }
                },
                currentUserPermission: {
                    fields: {
                        uuid: {},
                        access: {}
                    }
                }
            }
        }

        let query = graphqlify(queryObject)

        return $.ajax({
            url: `${API_PREFIX}/graphql/data?query=${query}`,
            type: 'GET',
            dataType: 'json',
        })
        .done((response) => {

            if (response.data && response.data.farmsV2 && response.data.farmsV2.length) {

                let farms = [];

                _.each(response.data.farmsV2, (farm) => {

                    farms.push(new FarmModel({
                        uuid: farm.uuid,
                        name: farm.name,
                        etag: farm.etag,
                        creatorUuid: farm.creator.uuid, // Legacy
                        creatorId: farm.creator.uuid,
                        access: farm.currentUserPermission.access,
                        timezone: farm.timezone,
                        latitude: farm.latitude,
                        longitude: farm.longitude,
                        ownerName: farm.creator.firstName + ' ' + farm.creator.lastName,
                        ownerEmail: farm.creator.email,
                        isOwnFarm: (this._user.get('uuid') === farm.creator.uuid)
                    }, {parse: true}))
                });

                FARMS.set(farms);
            }
        })
    }

    getUserDetails (permission) {

        let def = $.Deferred();
        let farmPermission = permission.find(permission => permission.get('granteeUuid') === permission.get('granterUuid'));
        let relatedUserModel = new UserModel({uuid: farmPermission.get('granteeUuid')});

        relatedUserModel.requestProfile().done((user) => {
            def.resolve(user);
        }).fail(() => {
            def.reject();
        });

        return def;
    }

    onFarmChange () {

        this._farm = this.model.get('farm');
        const country = this._user && this._user.getCountry();

        if (this._farm && !this._state.get('selectedFarm')) {
            this._state.set({selectedFarm: this._farm.get('uuid')});
        } else if (!this._farm && !this._state.get('selectedFarm') && this._user && this._user.get('uuid')) {

            let selectedFarmSetting = this._user.get('settings').getSetting('TAMS', 'LAST_FARM_SELECTED');

            this._state.set({selectedFarm: selectedFarmSetting.get('value')});
        } else if (this._state.get('selectedFarm') === 'dashboard') {
            this._farm = undefined;
        }

        if (country === CountryUtils.COUNTRY.FRANCE) {
            if (this._farm && this._farm.get('uuid')) {
                this.isPermissionAvailable(this._farm).then((permission) => {
                    if (permission) {
                        this.hfFarmPermissionCheck = true;
                    } else {
                        this.hfFarmPermissionCheck = false;
                    }
                });
            }
        }
    }

    onClickNavItem (event) {
        const navigationTo = event.currentTarget.dataset.nav

        const fields = this.model.get('fields');
        const isWeatherDisabled = fields && fields.length === 0

        if (navigationTo === 'nav_weather' &&  isWeatherDisabled) {
            return;
        }

        if (this._farm && this._farm.get('uuid')) {

            // Clear previous selections
            if (event.currentTarget.dataset.nav === 'nav_status-home') {
                SessionStorageUtil.set(SessionStorageUtil.KEY_NAMES.FIELDS, []);
            }

            let nav = event.currentTarget.dataset.nav;
            let route = this.navConfig[nav];

            if (this._user.isFeatureEnabled('HF_NEW')) {
                if (nav === 'nav_healthy-fields') {
                    route = 'healthy-fields/overview-new'
                }
            }

            router.navigateRelativelyToLocaleCode(`/farm/${this._farm.get('uuid')}/${route}`);
            this._state.set('currentSection', this.navConfig[nav]);
        }
    }

    /**
     * toggle one of the NavigationBarViews
     * secondary navigations and hide the rest
     * if you try to toggle an unknown all will be closed
     *
     * @param {String} attribute the state attribute to toggle
     */
    toggle (attribute = "") {
        let toggle = this._state.get(attribute);
        let state = {};
        let options = ['helpVisible', 'settingsVisible'];

        for (const option of options) {
            state[option] = attribute === option ? !toggle : false;
        }

        this._state.set(state);
        return !toggle;
    }

    onClickSettings () {

        if (this._farm && this._farm.get('uuid')) {
            if (this._user.isFeatureEnabled('USER_SETTINGS_NEW')) {
                router.navigateRelativelyToLocaleCode(`/farm/${this._farm.get('uuid')}/settings/user-profile`);
            } else {
                router.navigateRelativelyToLocaleCode(`/farm/${this._farm.get('uuid')}/settings/user`);
            }
        } else if (FARMS.length >1) {
            //@TODO: Ugly fix for navigation bar until new design is implemented
            this._farm = FARMS.models[1];
            this.onClickSettings();
        }
    }

    closeAll () {

        this.toggle();
    }

    onRouteFinish (params) {

        if (this.model && this.model.get('user') && this.model.get('user').get('uuid')) {

            let lastFarmSelectedUuid = this.model.get('user').get('settings')
                .getSetting('TAMS', 'LAST_FARM_SELECTED').get('value');

            if (params && params.routeNamedArgs && params.routeNamedArgs.farmUuid) {

                if (lastFarmSelectedUuid === params.routeNamedArgs.farmUuid) {

                    if (lastFarmSelectedUuid !== 'UNKNOWN') {
                        this._state.set('selectedFarm', lastFarmSelectedUuid);
                    }
                } else {

                    this.model.get('user').get('settings').setSetting(
                        'TAMS',
                        'LAST_FARM_SELECTED',
                        params.routeNamedArgs.farmUuid
                    );

                    this._state.set('selectedFarm', params.routeNamedArgs.farmUuid)
                }
            } else if (params && params.routeName === Alias.CROSS_FARM_DASHBOARD) {

                if (lastFarmSelectedUuid === 'dashboard') {
                    this._state.set('selectedFarm', 'dashboard')
                } else {

                    this.model.get('user').get('settings').setSetting(
                        'TAMS',
                        'LAST_FARM_SELECTED',
                        'dashboard'
                    );

                    if (this._state.get('selectedFarm')) {
                        this._state.set('selectedFarm', 'dashboard')
                    }
                }
            }
        }

        this.closeAll();
        let alias = params.routeName;
        let ev = router.getLastEvent();

        if (ev.routeNamedArgs && ev.routeNamedArgs.languageCode) {
            // toggle visibility
            if (ev.queryParams && _.isString(ev.queryParams.n)){
                // overwrite hidden routes
                Radio.channel('LAYOUT').trigger('NAVIGATIONBAR', (ev.queryParams.n === 'true'));
            } else {
                // use default settings for toggleparams.routeName;
                Radio.channel('LAYOUT').trigger('NAVIGATIONBAR', !_.includes(HIDDEN_ROUTES, alias));
            }
        } else {
            // use default settings for toggleparams.routeName;
            Radio.channel('LAYOUT').trigger('NAVIGATIONBAR', !_.includes(HIDDEN_ROUTES, alias));
        }

        this.onFarmChange();
        this.safeRender();
    }

    checkIsSettingsUrl(routerFragment) {

        const fragmentArr = router.getFragmentWithoutLocale(routerFragment || router.getCurrentPathFragment()).fragmentArr;

        const secondLastFragment = fragmentArr.slice(-2)[0];

        return (secondLastFragment && secondLastFragment.toLocaleLowerCase() === 'settings');
    }

    onBackClick() {

        const lastNotUserSettingsEvent = router.getReversedEvents().find((event) => {

            if (event.routeName === Alias.TASK_SCOUTING_CREATE) {
                return false;
            }

            if (event.routeName === Alias.SHOP_ORDER) {
                return false;
            }

            if (event && event.routeNamedArgs) {

                if (event.routeNamedArgs.farmUuid) {

                    if (FARMS.find({uuid: event.routeNamedArgs.farmUuid})) {

                        return !this.checkIsSettingsUrl(event.fragment);
                    }
                } else {
                    return !this.checkIsSettingsUrl(event.fragment);
                }
            }
        });

        if (lastNotUserSettingsEvent) {

            const fragment = router.getFragmentWithoutLocale(lastNotUserSettingsEvent.fragment).fragment;

            router.navigateRelativelyToLocaleCode('/' + fragment);
        }
        else {

            if (this._user && this._user.get('uuid')) {

                let lastFarmSelectedUuid = this.model.get('user').get('settings')
                    .getSetting('TAMS', 'LAST_FARM_SELECTED').get('value');

                if (lastFarmSelectedUuid === 'dashboard') {
                    router.navigateRelativelyToLocaleCode(`/farm/dashboard`);
                } else {
                    router.navigateRelativelyToLocaleCode(`/farm/${lastFarmSelectedUuid}/field/status`);
                }
            } else {
                router.navigateRelativelyToLocaleCode('/login');
            }
        }
    }

    fetchFarmPermissions(farm) {

        const permissions = new PermissionCollection();

        return new Promise((resolve, reject) => {
            permissions
                .setFilterParams(new BaseFilterModel({
                    objectUuid : farm.attributes.uuid,
                    objectType: 'FARM',
                }))
                .done((permissionCollection) => {
                    resolve(permissionCollection);
                })
                .fail(response => {
                    reject({message: __('PERMISSION_FETCH_FAILED'), response});
                });
        });
    }

    isPermissionAvailable (farm) {

        return new Promise((resolve, reject) => {
            this.fetchFarmPermissions
            (farm)
                .then(permissionCollection => {
                    let farmPermissions = permissionCollection;
                    let gigya_uuid = (ENV === 'qa') ? this.GIGYA_UUID_QA : this.GIGYA_UUID;
                    let existingPermission = farmPermissions.find((permission) => permission.get('granteeUuid') === gigya_uuid);
                    resolve(existingPermission);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    getFarmWithAccess () {

        let farmAccess = this._farm && this._farm.get('access');

        if((_.isUndefined(farmAccess) || farmAccess === null) && FARMS && FARMS.length > 0) {
            return FARMS.find({uuid: this._farm.get('uuid')});
        }

        return this._farm;
    }

    serializeData () {

        const isAgBalanceFeatureEnabled = !!CURRENT_FARM_FEATURES.find({
            name: 'AG_BALANCE',
        });
        const farmWithAccess = this.getFarmWithAccess();
        const farmAccess = farmWithAccess && farmWithAccess.get('access');
        const hasFullAccess = farmWithAccess && farmWithAccess.hasWriteOrShareOrLimitedAccess();
        const isCfd = (this._state.get('selectedFarm') === 'dashboard');
        const country = this._user && this._user.getCountry();
        const ev = router.getLastEvent();
        const farmUuid = (this._farm ? this._farm.get('uuid') : null);
        const userUuid = (this._user ? this._user.get('uuid') : null);
        const firstName = (this._user ? this._user.get('firstName') : null);
        const lastName = (this._user ? this._user.get('lastName') : null);
        const isStatusPageRoute = ev.routeName === Alias.STATUS;
        const isTaskRoute = ev.routeName === Alias.TASKS;
        const isHistoryRoute = ev.routeName === Alias.FIELDS;
        const isNotesRoute = ev.routeName === Alias.NOTES;
        const isWeatherRoute = ev.routeName === Alias.WEATHER;
        const isAnalyticsRoute = ev.routeName === Alias.FIELD_DATA_LAYERS;
        const isHealthyFieldsRoute = ev.routeName === Alias.HEALTHY_FIELDS_OVERVIEW;
        const isSustainabilityRoute = ev.routeName === Alias.SUSTAINABILITY;
        const isShopRoute = ev.routeName === Alias.SHOP_PRODUCTS;
        const isShopNavItemVisible = (this._user ? this._user.isFeatureEnabled('SHOP') : undefined);
        const isSustainabilityNavItemVisible = (this._user ? (this._user.isFeatureEnabled('SUSTAINABILITY') && isAgBalanceFeatureEnabled) : false);
        const fields = this.model.get('fields');
        const isWeatherDisabled = fields && fields.length === 0;
        let isHealthyFieldsNavItemVisible;

        if(!country) {
            this.hfFarmPermissionCheck = undefined;
        }

        // hide Healthy fields for France users unless they have enabled feature
        if (country === CountryUtils.COUNTRY.FRANCE) {
            if(this._farm && this._farm.get('uuid')) {
                isHealthyFieldsNavItemVisible = this.hfFarmPermissionCheck;
            }
        } else if (country === CountryUtils.COUNTRY.GERMANY) {
            isHealthyFieldsNavItemVisible = true;
        }

        // if user is on HF page and is not allowed redirect
        if (!_.isUndefined(this.hfFarmPermissionCheck) && !isHealthyFieldsNavItemVisible && isHealthyFieldsRoute && farmUuid && userUuid) {
            router.navigateRelativelyToLocaleCode(`/farm/${this._state.get('selectedFarm')}/field/status`);
        }

        const isWhiteThemeEnabled = this.checkIsSettingsUrl();

        return {
            farmUuid,
            userUuid,
            firstName,
            lastName,
            context: this,
            isDemoModeOff: (ENV !== 'demo'),
            state: this._state,
            farms: FARMS,
            farmSelectGroups: [
                {
                    filter: (m) => m.get('uuid') === 'dashboard',
                },
                {
                    title: __('NAVIGATION_BAR_VIEW.YOUR_FARMS'),
                    buttonLabel: __('NAVIGATION_BAR_VIEW.ADD_NEW_FARM'),
                    buttonCallback: this.onFarmAddClick,
                    parentThis: this,
                    filter: (m) => m.get('isOwnFarm'),
                },
                {
                    title: __('NAVIGATION_BAR_VIEW.SHARED_FARMS'),
                    buttonLabel: __('NAVIGATION_BAR_VIEW.REQUEST_ACCESS'),
                    buttonCallback: this.onRequestAccessClick,
                    user: this._user,
                    permissionRequests: this.permissionRequests,
                    filter: (m) => !m.get('isOwnFarm') && m.get('uuid') !== 'dashboard',
                },
            ],
            farmSelectItemTpl: (item) => {
                if(item && item.model) {
                    return farmSelectItemTpl({
                        farmName: item.label,
                        ownerName: item.model.get('ownerName'),
                        ownerEmail: item.model.get('ownerEmail'),
                        isOwnFarm: item.model.get('isOwnFarm'),
                        isDashboard: item.model.get('uuid') === 'dashboard',
                        access: item.model.serializeAccess()
                    });
                }
            },
            farmSelectValueTpl: (item) => {
                if(item && item.model) {
                    return farmSelectValueTpl({
                        farmName: item.label,
                        ownerName: item.model.get('ownerName'),
                        isOwnFarm: item.model.get('isOwnFarm'),
                        isDashboard: item.model.get('uuid') === 'dashboard',
                        access: item.model.serializeAccess()
                    });
                }
            },
            farmSelectFilterGetter: (farm) => farm ? _.compact([farm.get('name'), farm.get('ownerName'), farm.get('ownerEmail')]).join(' ').toLowerCase() : '',
            isTasksNavItemVisible: hasFullAccess || farmAccess === 'READ',
            isHistoryNavItemVisible: hasFullAccess || farmAccess === 'READ',
            isAnalyticsNavItemVisible: hasFullAccess || farmAccess === 'READ',
            isShopNavItemVisible: isShopNavItemVisible && hasFullAccess,
            isHealthyFieldsNavItemVisible: isHealthyFieldsNavItemVisible && hasFullAccess,
            isStatusPageRoute,
            isTaskRoute,
            isHistoryRoute,
            isNotesRoute,
            isWeatherRoute,
            isSustainabilityRoute,
            isAnalyticsRoute,
            isHealthyFieldsRoute,
            isShopRoute,
            isCfd,
            isWeatherDisabled,
            pendingPermissionRequestCount: this.getPendingPermissionRequestCount(),
            isWhiteThemeEnabled,
            isSustainabilityNavItemVisible,
            onBackClick: this.onBackClick,
        };
    }

    onStateChange () {

        if (this.model.get('user').get('settings')
            .getSetting('TAMS', 'LAST_FARM_SELECTED').get('value') !== this._state.get('selectedFarm')) {

            this.model.get('user').get('settings').setSetting(
                'TAMS',
                'LAST_FARM_SELECTED',
                this._state.get('selectedFarm')
            );
        }

        let ev = router.getLastEvent();

        if (this._state.get('selectedFarm') === 'dashboard') {

            if (ev.routeName && (ev.routeName !== Alias.CROSS_FARM_DASHBOARD)) {

                if (ev.routeName !== Alias.USER_SETTINGS &&
                    ev.routeName !== Alias.SCOUTING &&
                    ev.routeName !== Alias.WEATHER_STATIONS &&
                    ev.routeName !== Alias.CONNECTED_APPS &&
                    ev.routeName !== Alias.DOWNLOAD_HISTORY &&
                    ev.routeName !== Alias.DATA_ATTRIBUTION) {

                    router.navigateRelativelyToLocaleCode(`/farm/dashboard`);
                }
            }
        } else {

            if (ev.routeNamedArgs && ev.routeNamedArgs.farmUuid) {

                const fields = this.model.get('fields');

                if ((!fields || fields.length === 0) && ev.routeName === 'showFarmWeather') {
                
                    router.navigateRelativelyToLocaleCode(`/farm/${this._state.get('selectedFarm')}/field/status`);

                } else if (ev.routeNamedArgs.farmUuid !== this._state.get('selectedFarm')) {

                    let newRoute = ev.fragment.replace(ev.routeNamedArgs.farmUuid, this._state.get('selectedFarm')).substring(8);

                    router.navigateRelativelyToLocaleCode(newRoute);
                }
            } else if (!_.isNil(ev.routeName) && (ev.routeName !== Alias.FORWARD_LAYOUT)) {

                const ev = router.getLastEvent();
                let target = 'field/status';

                if (ev.routeName === Alias.TASKS) { target = this.navConfig.nav_tasks; }
                if (ev.routeName === Alias.FIELDS) { target = this.navConfig.nav_history; }
                if (ev.routeName === Alias.NOTES) { target = this.navConfig.nav_notes; }
                if (ev.routeName === Alias.WEATHER) { target = this.navConfig.nav_weather; }
                if (ev.routeName === Alias.FIELD_DATA_LAYERS) { target = this.navConfig.nav_analytics; }
                if (ev.routeName === Alias.SUSTAINABILITY) { target = this.navConfig.nav_sustainability; }
                if (ev.routeName === Alias.HEALTHY_FIELDS_OVERVIEW) { target = this.navConfig.nav_healthy-fields; }

                router.navigateRelativelyToLocaleCode(`/farm/${this._state.get('selectedFarm')}/${target}`);
            }
        }
    }

    onFarmAddClick() {

        this.ownedFarms = new FarmCollection(FARMS.filter(model => model.attributes.isFarmOwnedByMe));
        const modal = new FarmAddModal({
            collection: this.ownedFarms,
            onDone: (farm) => {
                farm.set({farmIcon: 'df-icon-farm', isOwnFarm: true});
                FARMS.add(farm);
                this.parentThis._state.set({selectedFarm: farm.get('uuid')});
            }
        });

        Radio.channel('NOTIFICATION').trigger('MODAL2', modal);
    }

    onRequestAccessClick() {

        const modal = new RequestAccessModalView({
            hash: {
                permissionRequests: this.permissionRequests,
                requesterEmail: this.user.get('email'),
                requesterCountry: this.user.get('country'),
                saveSuccess: () => {
                    modal.closeModal();
                },
                onCancel: () => {
                    modal.destroy();
                }
            }
        });

        Radio.channel('NOTIFICATION').trigger('MODAL2', modal);
    }

    onAttach () {

        this.bindUIElements();
    }

    onDestroy() {

        this._farm.off('change', this.updateContext, this);
        this._state.off('change', this.onStateChange, this);
        this._state.off('change', this.clearLocalStorage, this);
    }

    onRender() {

        const isWhiteThemeEnabled = this.checkIsSettingsUrl();

        this.$el.toggleClass('header2--white', isWhiteThemeEnabled);
    }
}


// WEBPACK FOOTER //
// ./shared/view/NavigationBarView/NavigationBarView.js